// The gate shown when the app must not be used — port of blockedScreen.dart,
// with one important addition.
//
// TWO REASONS, TWO OUTCOMES:
//
//   Account Inactive     an administrator disabled the account. Nothing the user
//                        can do; Exit App is the only action, as in the Dart.
//
//   Unauthorised Device  the account is bound to a different phone. The Dart
//                        dead-ends here too — but this is simply what happens
//                        when someone CHANGES PHONE, and it is the user's own
//                        account. Dead-ending means a customer who replaced
//                        their handset cannot use the app at all until an admin
//                        intervenes.
//
//                        So this offers the same OTP re-bind the LOGIN screen
//                        already performs (Flutter's Checker dialog): prove you
//                        control the registered mobile number, and the account
//                        moves to this phone.
//
// The OTP goes to the number already on the account, so it cannot be used to
// take over someone else's — you must be able to read that number's WhatsApp.
//
// Deliberately NOT themed from the server colours (the Dart hardcodes #101010
// too): the gate must look right even when the user record can't be trusted.

import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { useApp } from '../../../core/state/AppContext';
import { requestOtp, confirmDeviceBinding } from '../../../core/auth/loginFlow';
import { BLOCK_DEVICE } from '../../../core/auth/userGate';

const RED = '#FF5252';

export default function BlockedScreen() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { userData, user, setSession } = useApp();

  const title = state?.title || 'Access Blocked';
  const message = state?.message || 'This account cannot be used on this device.';
  const canRebind = state?.reason === BLOCK_DEVICE && !!user?.MobileNumber;

  const [step, setStep] = useState('blocked'); // blocked | otp | working
  const [expected, setExpected] = useState('');
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');

  const exit = () => { CapacitorApp.exitApp().catch(() => window.close()); };

  const sendOtp = async () => {
    setStep('working');
    setError('');
    const otp = await requestOtp(user.MobileNumber);
    if (!otp) {
      setError("Couldn't send the code. Check your connection and try again.");
      setStep('blocked');
      return;
    }
    setExpected(String(otp));
    setStep('otp');
  };

  const verify = async () => {
    if (typed.trim() !== expected) {
      setError('Incorrect code. Please check and try again.');
      return;
    }
    setStep('working');
    setError('');
    try {
      // Moves Users.DeviceName to THIS install and re-reads the row, so the
      // stored session matches the server and the gate passes next boot.
      const fresh = await confirmDeviceBinding(userData, user.MobileNumber, user.Password);
      if (fresh) setSession(fresh);
      navigate('/splash', { replace: true });
    } catch (e) {
      setError('Could not move the account to this phone. Please try again.');
      setStep('otp');
    }
  };

  const primaryBtn = {
    mt: 2, bgcolor: RED, color: '#fff', px: 3, py: 1.5, borderRadius: '10px',
    textTransform: 'none', '&:hover': { bgcolor: RED },
  };

  return (
    <Box sx={{
      height: '100%',
      overflowY: 'auto',
      bgcolor: '#101010',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3.5,
    }}
    >
      <Box sx={{ maxWidth: 420, textAlign: 'center', width: '100%' }}>
        <LockOutlinedIcon sx={{ fontSize: 72, color: RED }} />
        <Typography sx={{ mt: 2.5, color: '#fff', fontSize: 22, fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>
          {message}
        </Typography>

        {step === 'working' && (
          <CircularProgress sx={{ color: RED, mt: 4 }} />
        )}

        {step === 'blocked' && canRebind && (
          <>
            <Typography sx={{ mt: 3, color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>
              Changed your phone? Verify the mobile number on the account and it
              will move to this one.
            </Typography>
            {error && (
              <Typography sx={{ mt: 2, color: RED, fontSize: 14 }}>{error}</Typography>
            )}
            <Button onClick={sendOtp} startIcon={<PhoneAndroidIcon />} sx={primaryBtn}>
              This is my new phone
            </Button>
            <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
              {`A code will be sent on WhatsApp to ${String(user.MobileNumber).slice(-4).padStart(10, '•')}`}
            </Typography>
          </>
        )}

        {step === 'otp' && (
          <>
            <Typography sx={{ mt: 3, color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>
              Enter the code sent on WhatsApp.
            </Typography>
            <TextField
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
              inputProps={{ inputMode: 'numeric', maxLength: 6, style: { textAlign: 'center', letterSpacing: 6 } }}
              sx={{
                mt: 2,
                width: 200,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&:hover fieldset': { borderColor: '#fff' },
                  '&.Mui-focused fieldset': { borderColor: RED },
                },
              }}
            />
            {error && (
              <Typography sx={{ mt: 2, color: RED, fontSize: 14 }}>{error}</Typography>
            )}
            <Button onClick={verify} disabled={!typed.trim()} sx={primaryBtn}>
              Use this phone
            </Button>
          </>
        )}

        {step !== 'otp' && !canRebind && (
          <Typography sx={{ mt: 1.5, color: '#fff', fontSize: 15, fontWeight: 600 }}>
            Please contact the administrator.
          </Typography>
        )}

        {step !== 'working' && (
          <Button
            onClick={exit}
            startIcon={<CloseIcon />}
            sx={{
              mt: 2,
              color: 'rgba(255,255,255,0.65)',
              textTransform: 'none',
              display: 'block',
              mx: 'auto',
            }}
          >
            Exit App
          </Button>
        )}
      </Box>
    </Box>
  );
}
